import React, { useEffect, useState } from "react";

import {
  mdiFolderStar,
  mdiHelpBoxMultiple,
  mdiPanorama,
  mdiPuzzle,
  mdiViewCarousel,
  mdiViewModule,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import ProjectAPI from "../../../../api/ProjectAPI";
import Breadcrumbs from "../../../../components/utils/Breadcrumbs";
import ContainerHeader from "../../../../components/utils/ContainerHeader";
import { COMPONENT_TYPES } from "../../../../constants/componentType";
import isPlatformTV from "../../../../utils/platform";
import slugify from "../../../../utils/slug";

import type { JSX } from "react";

import type {
  NewProjectPage,
  ProjectPage,
} from "../../../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../../../components/routes/PlatformRoutes";

const { MOSAIC, CAROUSEL, WIDGET, HIGHLIGHT, FAQ, SHORTCUT } = COMPONENT_TYPES;

interface PageEditHeaderProps {
  onSave: (updatedPage: ProjectPage | NewProjectPage) => void;
}

export default function PageEditHeader({
  onSave,
}: PageEditHeaderProps): JSX.Element {
  const [pageLabel, setPageLabel] = useState<string | undefined>(undefined);
  const [isEnabledChange, setIsEnabledChange] = useState<boolean>();
  const {
    projectSlug,
    pageId,
    platform = "web",
  } = useParams<PlatformRoutesParams>();

  const isNew = pageId === "new";

  const { t } = useTranslation("editor-platform");
  const history = useHistory<{
    newPageRank?: number;
  }>();

  const { newPageRank } = history.location.state || {};

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: projectPageData, isFetching } = useQuery(getProjectPageQuery);

  const isEnabled = isEnabledChange ?? projectPageData?.enabled ?? false;
  const stateDisabledReason =
    projectPageData?.default && isEnabled
      ? t("pages.homepageAlwaysEnabled")
      : undefined;

  const handleLabelChange = (
    target: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>["target"],
  ): void => {
    setPageLabel(target.value);
  };

  useEffect(() => {
    if (projectPageData && pageLabel === undefined) {
      setPageLabel(projectPageData.label);
    } else if (pageLabel === undefined && isNew) {
      setPageLabel(t("pages.new"));
    }
  }, [pageLabel, projectPageData, isNew, t]);

  const dropdownItemClassName = "text-muted-foreground";

  const handleCreate = {
    label: t("createComponent"),
    options: [
      {
        label: t("mosaic"),
        icon: <Icon path={mdiViewModule} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${MOSAIC}/new`,
          });
        },
        className: dropdownItemClassName,
      },
      {
        label: t("carousel"),
        icon: <Icon path={mdiViewCarousel} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${CAROUSEL}/new`,
          });
        },
        className: dropdownItemClassName,
      },
      {
        label: t("editor-widget:widget"),
        icon: <Icon path={mdiPuzzle} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${WIDGET}/new`,
          });
        },
        className: dropdownItemClassName,
      },
      {
        label: t("editor-highlight:highlight"),
        icon: <Icon path={mdiPanorama} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${HIGHLIGHT}/new`,
          });
        },
        className: dropdownItemClassName,
      },
      {
        label: t("faq"),
        icon: <Icon path={mdiHelpBoxMultiple} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${FAQ}/new`,
          });
        },
        className: dropdownItemClassName,
        isDisabled: isPlatformTV(platform),
      },
      {
        label: t("shortcut"),
        icon: <Icon path={mdiFolderStar} size={0.9375} />,
        onClick: (): void => {
          history.push({
            pathname: `${platform}/${SHORTCUT}/new`,
          });
        },
        className: dropdownItemClassName,
      },
    ],
  };

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("title.layout"),
      url: "/layout",
    },
    {
      name: isNew ? t("pages.new") : projectPageData?.label,
    },
  ];

  const handleSave = async (): Promise<void> => {
    if (!projectPageData && !isNew) return;

    if (isNew) {
      const newPage = {
        components_platform: ["web" as const],
        default: false,
        storefront_project_uuid: pageId,
        rank: newPageRank ?? 0,
        enabled: isEnabled,
        label: pageLabel ?? "",
        translations: [
          {
            slug: slugify(pageLabel ?? ""),
            language: "fr" as const,
            title: pageLabel ?? "",
            default: true,
          },
        ],
      };

      onSave(newPage);
    } else if (projectPageData) {
      const updatedPage = {
        ...projectPageData,
        label: pageLabel ?? "",
        enabled: isEnabled,
      };

      onSave(updatedPage);
    }
  };

  return (
    <React.Fragment>
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        className="!mb-0"
        itemStatus={isEnabled ? "active" : "stop"}
        onChangeLabel={handleLabelChange}
        onCreate={isNew ? undefined : handleCreate}
        onSave={handleSave}
        stateDisabledReason={stateDisabledReason}
        item={{
          label: isFetching ? "..." : pageLabel,
        }}
        onChangeState={
          isNew || projectPageData ? setIsEnabledChange : undefined
        }
      />
    </React.Fragment>
  );
}
